// GuideEditor.js
import React, { useEffect, useState } from "react";
import { TextField, Container, Box, Paper, Typography, CircularProgress } from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import InsightEditor from "./InsightEditor";
import ExperiencesAPI from "../services/ExperiencesAPI";
import SystemOptionsDropdown from "./SystemOptionsDropdown";
import { useContent } from "../context/ContentContext";
import { elementOptions, environmentOptions } from "../data/environments";
import { useExperiences } from "../context/ExperiencesContext";
import { AppButton } from "./ui";

import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";

// Helper to generate a UUID
const generateId = () => crypto.randomUUID();


const flattenGuideData = (guide) => {

  console.log("0 - FLATTEN GUIDE", guide);
  

  let insightDatasUpdated = guide.insightDatas
      ? guide.insightDatas
          .filter((item) => item !== null)
          .map((item) => item.data)
      : [];

  console.log("0 - INSIGHT DATAS UPDATED", insightDatasUpdated)

  return {
    ...guide,
    insightDatas: insightDatasUpdated,
  };
};


const GuideEditor = ({ initialGuide }) => {
  const [guide, setGuide] = useState({
    name: "New Guide",
    id: generateId(),
    insightDatas: [],
    insightDataIds: [],
  });

  const [systemData, setSystemData] = useState(null);
  const { getItem, getThumbnail } = useContent();
  const [environments, setEnvironments] = useState([]);
  const [elements, setElements] = useState([]);
  const { postGuide } = useExperiences();

  const [guideStatus, setGuideStatus] = useState(null); // e.g., "Saving guide…", "Guide saved ✅"
  const [insightStatus, setInsightStatus] = useState(null); // e.g., "Uploading insight 2/5…", "All insights uploaded ✅"
  const [doneStatus, setDoneStatus] = useState(null); // e.g., "Submitted successfully!"

  const startStatuses = () => {
    setDoneStatus(null);
    setGuideStatus(null);
    setInsightStatus(null);
  };

  const finishStatuses = () => {
    setGuideStatus(null);
    setInsightStatus(null);
    setDoneStatus("Submitted successfully!");
    // Optionally auto-hide after a few seconds:
    // setTimeout(() => setDoneStatus(null), 3000);
  };


  useEffect(() => {
    console.log()
    if (initialGuide) {
      setGuide(flattenGuideData(initialGuide));
      console.log("1 - INITIAL GUIDE BEING SET", initialGuide)
    }

    if(guide.contextId){
      getItem("systems", guide.contextId).then((data) => {
        // console.log("system data", data);
        setSystemData(data);
      });
    }
     


  }, [initialGuide]);


  useEffect(() => {
    console.log("2 - GUIDE IS UPDATED", guide)
      if(guide.contextId){
      getItem("systems", guide.contextId).then((data) => {
        // console.log("system data", data);
         setSystemData(data);
    });
  }
  }, [guide]);


  useEffect(() => {
    if(systemData){
      // console.log("Getting envs and elements", systemData)
      // console.log("Environment options", environmentOptions(systemData));
      setEnvironments(environmentOptions(systemData));
      setElements(elementOptions(systemData));
    }
  }, [systemData])
  

  // Function to update auto-set triggers (for triggers with type "InsightComplete")
  const updateAutoCompleteTriggers = (insights) => {
    console.log("3 - UPDATE AUTO COMPLETE TRIGGER", insights)
    return insights.map((insight, insightIdx, arr) => {
      // No triggers? nothing to do'
      console.log(insight.id)
      console.log(insightIdx)
      if (!Array.isArray(insight.insightTriggers) || insight.insightTriggers.length === 0) {
        return insight;
      }

      let firstType2Handled = false; // guard the “first only” rule

      const updatedTriggers = insight.insightTriggers.map((trigger) => {
        if (trigger.insightTriggerType === 2) {
          if (!firstType2Handled) {
            firstType2Handled = true;

            // If this insight has a predecessor, auto-wire to it …
            if (insightIdx > 0) {
              const prev = arr[insightIdx - 1];
              return {
                ...trigger,
                triggerId: prev.id,
                name: prev.name || "Previous Insight",
              };
            }

            // … otherwise (it’s the very first insight) blank the fields
            return { ...trigger, triggerId: "", name: "" };
          }

          // Any later type-2 trigger stays exactly as the user set it
          return trigger;
        }

        // Non-type-2 triggers are untouched
        return trigger;
      });

      console.log("4 - AUTO UPDATE", insights)

      return { ...insight, insightTriggers: updatedTriggers };
    });
  };


  

  // Called when an insight changes
  const handleInsightChange = (index, updatedInsight) => {
    // console.log(guide)
    const newInsights = [...guide.insightDatas];
    newInsights[index] = updatedInsight;
    // Auto-update triggers for all insights.
        console.log(" INSIGHTs changed", newInsights);

    const updatedInsights = updateAutoCompleteTriggers(newInsights);
    setGuide({ ...guide, insightDatas: updatedInsights });
  };

  const handleCloneInsight = (index) => {
    const source = guide.insightDatas[index];
    console.log("CLONING INSIGHT", source)
    // deep-copy the object and give it a fresh ID
    const clone = {
      ...JSON.parse(JSON.stringify(source)),
      id: generateId(),
      name: `${source.name || "Untitled"} (Copy)`,
    };

    const newInsights = [...guide.insightDatas];
    newInsights.splice(index + 1, 0, clone); // insert just after original

    

    console.log("NEW INSIGHTS", newInsights)
    setGuide({
      ...guide,
      insightDatas: updateAutoCompleteTriggers(newInsights),
    });
  };

  // Add a new insight (with default values)
  const handleAddInsight = () => {
    const newInsight = {
      id: generateId(),
      name: "",
      text: "",
      answerKey: "",
      insightType: "", // Let the user select (for example, "Instruction", "Information", "Assessment")
      dismissable: false,
      insightTriggers: [],
      startConditions: [],
      endConditions: [],
    };
    const newInsights = [...guide.insightDatas, newInsight];
    // Update auto-set triggers in case the new insight causes a change.
        console.log("ADD INSIGHT", newInsights);

    setGuide({
      ...guide,
      insightDatas: updateAutoCompleteTriggers(newInsights),
    });
  };

  // Delete an insight and recalc auto-set triggers.
  const handleDeleteInsight = (index) => {
    const newInsights = [...guide.insightDatas];
    newInsights.splice(index, 1);
    console.log("DELETE INSIGHT", newInsights)
    setGuide({
      ...guide,
      insightDatas: updateAutoCompleteTriggers(newInsights),
    });
  };

  // When the drag-and-drop operation ends, reorder the list and update auto-set triggers.
  const onDragEnd = (result) => {
    if (!result.destination) return;
    const newInsights = Array.from(guide.insightDatas);
    const [removed] = newInsights.splice(result.source.index, 1);
    newInsights.splice(result.destination.index, 0, removed);
    setGuide({
      ...guide,
      insightDatas: updateAutoCompleteTriggers(newInsights),
    });
  };

  const prepareInsightForSave = (raw) => ({
    ...raw,
    startConditions: normaliseCondArray(raw.startConditions),
    endConditions: normaliseCondArray(raw.endConditions),
  });

  const normaliseCondArray = (arr = []) =>
    arr.map((c) => {
      // keep environment IDs as strings
      if (c.InsightConditionType === 0 || c.InsightConditionType === 1) {
        return {
          ...c,
          conditions: Array.isArray(c.conditions)
            ? c.conditions
            : c.conditions
            ? [c.conditions] // wrap the ID
            : [], // keep empty if nothing selected
        };
      }

      // all other types: split on '|'
      return {
        ...c,
        conditions: Array.isArray(c.conditions)
          ? c.conditions
          : c.conditions
              .split("|")
              .map((s) => s.trim())
              .filter(Boolean),
      };
    });
  // When the guide is submitted, prepare the payload as before.
  const handleSubmit = async () => {
    const preparedInsights = guide.insightDatas.map(prepareInsightForSave);
    const guidePayload = {
      ...guide,
      insightDataIds: preparedInsights.map((i) => i.id),
    };
    delete guidePayload.insightDatas;

    try {
      startStatuses();

      // 1) Save guide
      setGuideStatus("Saving guide…");
      try {
        await postGuide(guidePayload); // uses context; no refetch needed
        setGuideStatus("Saving Guide...");
      } catch (error) {
        setGuideStatus("Guide save failed ❌");
        console.error("Error saving guide:", error);
        return; // stop if guide failed to save
      }

      // 2) Save insights (one by one with progress)
      const total = preparedInsights.length;
      for (let i = 0; i < total; i++) {
        setInsightStatus(`[${i + 1}/${total}] Uploading insight ${preparedInsights[i].name} …`);
        try {
          await ExperiencesAPI.post("insights", { ...preparedInsights[i] }, { requiresAuth: true });
        } catch (error) {
          console.error(`Error creating insight ${i + 1}:`, error);
          setInsightStatus(`Insight ${i + 1} failed ❌`);
          // continue or break — your call. Here we continue:
        }
      }
      if (total > 0) setInsightStatus("Guide saved. All insights uploaded ✅");

      // 3) Done
      finishStatuses();
    } catch (error) {
      console.error("Submission error:", error);
      setGuideStatus("Submission failed ❌");
    }
  };


  return (
    <Container  sx={{ marginTop: 4 }}>
      <TextField
        label="Guide Name"
        value={guide.name}
        onChange={(e) => setGuide({ ...guide, name: e.target.value })}
        fullWidth
        sx={{ marginBottom: 2 }}
      />
      <SystemOptionsDropdown
        value={guide.contextId || ""}
        onChange={(newSystemId) => setGuide({ ...guide, contextId: newSystemId })}
      />
      <AppButton
        sx={{ position: "fixed", top: 120, right: 30, zIndex: 10000 }}
        kind="primary"
        onClick={handleSubmit}
      >
        Save Guide
      </AppButton>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="insightList">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {guide.insightDatas.map((insight, index) => (
                <Draggable key={insight.id} draggableId={insight.id} index={index}>
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={{
                        ...provided.draggableProps.style,
                        marginBottom: 8,
                      }}
                    >
                      <div {...provided.dragHandleProps}>
                        <strong>::</strong>
                      </div>
                      <InsightEditor
                        key={insight.id}
                        insight={insight}
                        index={index}
                        onChange={handleInsightChange}
                        onClone={handleCloneInsight}
                        onDelete={handleDeleteInsight}
                        allInsights={guide.insightDatas}
                        environments={environments}
                        elements={elements}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <AppButton kind="secondary" onClick={handleAddInsight} sx={{ marginRight: 2 }}>
        Add Insight
      </AppButton>

      {(guideStatus || insightStatus || doneStatus) && (
        <Box sx={{ position: "fixed", top: 180, right: 30, zIndex: 10000 }}>
          <Paper elevation={3} sx={{ p: 1.5, minWidth: 260 }}>
            {guideStatus && (
              <Box sx={{ display: "flex", alignItems: "center", mb: insightStatus || doneStatus ? 0.5 : 0 }}>
                {guideStatus.includes("Saving") && <CircularProgress size={16} sx={{ mr: 1 }} />}
                <Typography variant="body2">{guideStatus}</Typography>
              </Box>
            )}
            {insightStatus && (
              <Box sx={{ display: "flex", alignItems: "center", mb: doneStatus ? 0.5 : 0 }}>
                {insightStatus.includes("…") && <CircularProgress size={16} sx={{ mr: 1 }} />}
                <Typography variant="body2">{insightStatus}</Typography>
              </Box>
            )}
            {doneStatus && (
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {doneStatus}
                <IconButton
                  aria-label="Close"
                  size="small"
                  onClick={() => {
                    setDoneStatus(null);
                    setGuideStatus(null);
                    setInsightStatus(null);
                  }}
                  sx={{
                    position: "absolute",
                    top: 0,
                    right: 3,
                    height: "100%",
                    width: "40px",
                    // Give it a pill so it's visible on any bg
                    bgcolor: (theme) => theme.palette.action.hover,
                    "&:hover": { bgcolor: (theme) => theme.palette.action.selected },
                    color: "var(--color-text-secondary, rgba(0, 0, 0, 0.7))",
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Typography>
            )}
          </Paper>
        </Box>
      )}
    </Container>
  );
};

export default GuideEditor;
